import { useMutation } from '@tanstack/react-query'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { ApiError, messageForError } from '../api/errors'
import { createSession, type SessionResult } from '../api/sessions'
import { updateUser } from '../api/users'
import { forgetResetToken, readResetToken } from '../auth/reset-link'
import { useSession } from '../state/session'
import { AuthCard } from './ui/AuthCard'
import { Button } from './ui/Button'
import { FormError } from './ui/FormError'
import { TextField } from './ui/TextField'
import { TextLink } from './ui/TextLink'
import { useFocusWhenEnabled } from './ui/useFocusWhenEnabled'

export interface ResetPasswordFormProps {
  /** Where a finished reset lands, signed in. `data-redirect-to`. */
  redirectTo?: string
  /** Where to ask for a fresh link. `data-forgot-url`. */
  forgotUrl?: string
  /** A hosted logo to show above the heading; omit for none. `data-logo-url`. */
  logoUrl?: string
  heading?: string
}

const MISSING_LINK_MESSAGE =
  'This page needs the link from your email. Open it again, or request a new one.'
const SPENT_LINK_MESSAGE =
  'This link has expired or has already been used. Request a new one and try again.'

/**
 * Chooses a new password from a reset link.
 *
 * The link's token is read from the fragment once, at mount, and dropped from
 * the address before anything else happens. It is exchanged only when the
 * parent submits a password: the token is single-use, and email security
 * scanners and link previews open links. The exchange is a login with the
 * token as its credential, and the session it yields pays for the one request
 * that matters — setting the password as that user — and is written to the
 * store only once that succeeds. A retry after a rejected password reuses the
 * exchanged session rather than spending the link again.
 *
 * The same link serves a parent choosing their first password (an account
 * provisioned at checkout) and one resetting a forgotten one; the record does
 * not say which, so the wording suits both.
 */
export function ResetPasswordForm({
  redirectTo = '/account',
  forgotUrl = '/forgot-password',
  logoUrl,
  heading = 'Choose a password',
}: ResetPasswordFormProps) {
  const signIn = useSession((state) => state.signIn)

  // Read once, at mount: the address is scrubbed straight after, so nothing
  // later on the page — a copied link, a referrer, a marketing tag — sees it.
  const [token] = useState(() => readResetToken(window.location.hash))
  useEffect(() => {
    if (token) forgetResetToken()
  }, [token])

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [spent, setSpent] = useState(false)
  const passwordRef = useRef<HTMLInputElement>(null)
  const confirmRef = useRef<HTMLInputElement>(null)

  // The session the link was exchanged for. The token is single-use, so a
  // retry after a rejected password must not exchange it again.
  const exchanged = useRef<SessionResult | null>(null)

  const reset = useMutation({
    mutationFn: async (token: string) => {
      exchanged.current ??= await createSession({ token })
      const { accessToken, userId } = exchanged.current
      const user = await updateUser(userId, { password }, { auth: { accessToken, userId } })
      return { ...exchanged.current, user }
    },
    // Only now is the session worth keeping: the password is set, and the
    // parent lands signed in.
    onSuccess: (result) => {
      signIn(result)
      window.location.assign(redirectTo)
    },
    onError: (cause) => {
      // A 404 before the exchange has succeeded is the link itself: spent,
      // expired or mistyped, which the API does not distinguish.
      if (!exchanged.current && cause instanceof ApiError && cause.status === 404) {
        setSpent(true)
        return
      }
      setError(messageForError(cause))
      if (cause instanceof ApiError && cause.status === 422) {
        // The strength rule — the one thing the parent can fix here.
        focusPassword()
      } else {
        console.error('[codeverse] password reset failed', cause)
      }
    },
  })

  const focusPassword = useFocusWhenEnabled(reset.isPending, () => passwordRef.current)

  const canSubmit = password !== '' && confirm !== '' && !reset.isPending

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit || !token) return
    if (password !== confirm) {
      setError('Those passwords don’t match.')
      confirmRef.current?.focus()
      return
    }
    setError(null)
    reset.mutate(token)
  }

  if (!token || spent) {
    return (
      <AuthCard heading={heading} logoUrl={logoUrl}>
        <FormError data-testid="reset-unusable">
          {token ? SPENT_LINK_MESSAGE : MISSING_LINK_MESSAGE}
        </FormError>
        <p className="mt-5 text-center">
          <TextLink href={forgotUrl} data-testid="request-link">
            Request a new link
          </TextLink>
        </p>
      </AuthCard>
    )
  }

  return (
    <AuthCard heading={heading} logoUrl={logoUrl}>
      <form onSubmit={onSubmit} noValidate>
        {error && <FormError data-testid="reset-error">{error}</FormError>}

        <div className="mt-6 flex flex-col gap-3">
          <TextField
            ref={passwordRef}
            label="New password"
            placeholder="New password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={reset.isPending}
            data-testid="password"
          />

          <TextField
            ref={confirmRef}
            label="Confirm new password"
            placeholder="Confirm new password"
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            disabled={reset.isPending}
            data-testid="confirm"
          />
        </div>

        <div className="mt-6">
          <Button type="submit" block disabled={!canSubmit} data-testid="submit">
            {reset.isPending ? 'Saving…' : 'Save password'}
          </Button>
        </div>
      </form>
    </AuthCard>
  )
}
